import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { GenericHelp } from '/components/common'

import './index.less'
import api from 'services/api'
import { getCurrentAttempts } from 'services/smsAttempts'
import MaskedInput from 'antd-mask-input'
import {
  Typography,
  Button,
  Row,
  Col,
  Alert,
  Form,
  Icon,
  notification,
} from 'antd'
const { Text } = Typography

const SMS_HELP_LINK = 'https://pt.surveymonkey.com/r/PRO_SMS'

const resendAlertMessage = resendCountdown => (
  <div>
    Não recebeu o código? Em <strong>{resendCountdown}s</strong> você pode
    enviar outro SMS
  </div>
)

const backToSmsComponentMessage = nextSmsReadyCallback => (
  <div>
    Ainda não tem um código? Você pode enviar um{' '}
    <strong
      style={{ cursor: 'pointer' }}
      onClick={() => nextSmsReadyCallback()}
    >
      clicando aqui
    </strong>
  </div>
)

export const CodeVerification = ({
  nextSmsReadyCallback,
  verifiedCodeCallback,
  pin,
  canSendANewSms,
}) => {
  const [state, setState] = useState({
    resendCountdown: 250,
    isSubmitting: false,
    countDone: false,
  })
  const [visibleAlert, setVisibleAlert] = useState(false)
  const [code, setCode] = useState('')
  let progressInterval = null

  const currentAttempts = getCurrentAttempts()

  const handleChange = e => {
    setCode(e.target.value)
  }

  useEffect(() => {
    // If the user clicked on the alert of the previous component, the timer will not be created and he can return to the sms sending component
    if (!canSendANewSms) {
      // If the timer doesnt exist in the local storage, it will be created with the current moment added 250 seconds
      if (!localStorage.getItem('timerBeforeNextSms')) {
        const timerBeforeNextSms = Date.now() + 250000
        localStorage.setItem('timerBeforeNextSms', timerBeforeNextSms)
      } else {
        const timeLeft =
          (localStorage.getItem('timerBeforeNextSms') - Date.now()) / 1000
        setState({ ...state, resendCountdown: timeLeft.toFixed() })
      }

      progressInterval = setInterval(checkProgress, 1000)
    } else {
      return
    }
    return () => {
      clearInterval(progressInterval)
    }
  }, [])

  const checkProgress = () => {
    const timerBeforeNextSms = localStorage.getItem('timerBeforeNextSms')

    if (timerBeforeNextSms > Date.now()) {
      setState(({ resendCountdown }) => ({
        resendCountdown: resendCountdown - 1,
      }))
    }
    if (timerBeforeNextSms <= Date.now()) {
      localStorage.removeItem('timerBeforeNextSms')
      clearInterval(progressInterval)
      setState({ ...state, countDone: true })
      nextSmsReadyCallback()
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setState({ ...state, isSubmitting: true })

    let normalizedCode = code.replace('-', '')

    try {
      const { data } = await api.post(
        `/delivery-bundle/${pin}/validate-customer`,
        {
          data: normalizedCode,
          type: 'sms',
          browserIdentifier: localStorage.getItem('cid'),
        },
      )

      if (data.code === 'NotAcceptable') {
        notification.open({
          message:
            'Ocorreu um erro. Por favor, entre em contato com o suporte.',
          icon: <Icon type='frown' style={{ color: '#ff0000' }} />,
        })
      } else if (data.code === 'InvalidCode') {
        setVisibleAlert(true)
      } else {
        verifiedCodeCallback({
          fetchedProducts: data.products,
          accessToken: data.accessToken,
        })
        // If the validation succeeded, clear the `smsAttempts` variable to "hide" the help component
        localStorage.removeItem('smsAttempts')
      }
    } catch (error) {
      notification.open({
        message:
          'Ocorreu um erro interno. Por favor, tente novamente em alguns minutos',
        icon: <Icon type='frown' style={{ color: '#ff0000' }} />,
      })
    }

    setState({ ...state, isSubmitting: false })
  }

  return (
    <Row style={{ height: '100%' }}>
      <Col
        xs={{ span: 20, offset: 2 }}
        lg={{ span: 13, offset: 7 }}
        xl={{ offset: 7 }}
        xxl={{ offset: 6 }}
        id='code-form'
      >
        <Text style={{ fontSize: 20 }}>
          Enviamos um SMS para seu número, ele contém 6 digitos e você deve
          digitar abaixo.
        </Text>

        <Form onSubmit={handleSubmit}>
          <MaskedInput
            mask='111-111'
            required
            autoComplete='one-time-code'
            inputMode='numeric'
            name='code'
            onChange={handleChange}
            value={code}
            id='code-input'
          />
          {visibleAlert && (
            <Alert message='Código Inválido' type='error' showIcon />
          )}
          {state.resendCountdown > 0 && (
            <Alert
              message={
                canSendANewSms
                  ? backToSmsComponentMessage(nextSmsReadyCallback)
                  : resendAlertMessage(state.resendCountdown)
              }
              type='warning'
              showIcon
              style={{
                margin: '10px 0',
                display: state.countDone ? 'none' : 'block',
              }}
            />
          )}

          <Button
            type='primary'
            htmlType='submit'
            id='submit'
            className='btn'
            block
            loading={state.isSubmitting}
          >
            Validar Código
          </Button>
        </Form>
        {currentAttempts >= 3 && <GenericHelp href={SMS_HELP_LINK} />}
      </Col>
    </Row>
  )
}

CodeVerification.propTypes = {
  /** Function to be called when the code is verified by the server */
  verifiedCodeCallback: PropTypes.func.isRequired,

  /** Function to be called when the next sms counter ends and the user can send a new sms  */
  nextSmsReadyCallback: PropTypes.func.isRequired,

  /** Delivery bundle pin */
  pin: PropTypes.string,

  /** If the user clicked on the alert of the previous component, he can return to the sms sending component  */
  canSendANewSms: PropTypes.bool,
}
